<script lang="ts">
  import { Check, X } from 'lucide-svelte';
  import { magicBento } from '$lib/actions/magicBento';
  import MagicSpotlight from '$lib/components/MagicSpotlight.svelte';

  // Toggle magic bento effects - set to false to disable
  const enableMagicBento = true;

  // Buy Now Modal State
  let showBuyModal = false;
  let selectedPlan: any = null;
  let customerEmail = '';
  let isProcessing = false;
  let errorMessage = '';

  const plans = [
    {
      id: 1,
      name: 'OpenAI',
      price: '39.99',
      description: 'Access all of your favorite latest OpenAI models for the cheapest price',
      features: [
        'GPT-5',
        'Sora 2',
        'Codex-CLI',
        'And much more...'
      ],
      popular: false
    },
    {
      id: 2,
      name: 'Anthropic',
      price: '49.99',
      description: 'All of the latest Claude models; plug-and-play with Claude Code',
      features: [
        'Claude 4.5 Sonnet',
        'Claude 4.1 Opus',
        'Claude Code',
        'And much more...'
      ],
      popular: true
    },
    {
      id: 3,
      name: 'Google Gemini',
      price: '44.99',
      description: 'Nano Banana image generation, the newest Gemini models, and more',
      features: [
        'Gemini 2.5 Pro',
        'Nano Banana',
        'Gemini-CLI',
        'And much more...',
      ],
      popular: false
    }
  ];

  function openBuyModal(plan: any) {
    selectedPlan = plan;
    showBuyModal = true;
    errorMessage = '';
    customerEmail = '';
  }

  function closeBuyModal() {
    showBuyModal = false;
    selectedPlan = null;
    customerEmail = '';
    errorMessage = '';
    isProcessing = false;
  }

  async function handleBuyNow() {
    if (!customerEmail || !selectedPlan) return;

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      errorMessage = 'Please enter a valid email address';
      return;
    }

    isProcessing = true;
    errorMessage = '';

    try {
      // Use backend API URL (defaults to same origin, or localhost:3000 for dev)
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';

      const response = await fetch(`${apiUrl}/api/payment/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: selectedPlan.id,
          customer_email: customerEmail,
          quantity: 1,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payment');
      }

      // Redirect to OxaPay payment page
      window.location.href = data.payment_url;
    } catch (error: any) {
      console.error('Payment creation error:', error);
      errorMessage = error.message || 'Failed to create payment. Please try again.';
      isProcessing = false;
    }
  }

  function handleKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !isProcessing) {
      handleBuyNow();
    }
  }
</script>

<MagicSpotlight enabled={enableMagicBento} glowColor="255, 255, 255" spotlightRadius={300} />

<section id="pricing" class="py-20 md:py-32 px-4 md:px-8 border-t border-white/5">
  <div class="max-w-7xl mx-auto">
    <div class="text-center mb-12 md:mb-20">
      <h2 class="text-white mb-4 md:mb-6 text-4xl sm:text-5xl md:text-6xl">
        Corporate access
      </h2>
      <p class="text-slate-500 text-base md:text-lg max-w-2xl mx-auto leading-relaxed px-4">
        Enterprise API keys for the world's most advanced AI models. All plans include dedicated support and warranty protection.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
      {#each plans as plan}
        <div
          use:magicBento={{
            enableParticles: enableMagicBento,
            enableTilt: false,
            enableMagnetism: false,
            enableClickRipple: enableMagicBento,
            particleCount: 8,
            glowColor: '255, 255, 255'
          }}
          class="relative bg-white/5 backdrop-blur-sm rounded-xl border {plan.popular ? 'border-white/30 scale-[1.02]' : 'border-white/10'} magic-bento-card flex flex-col"
          style="box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.2); --glow-x: 50%; --glow-y: 50%; --glow-intensity: 0; --glow-color: 255, 255, 255;"
        >
          {#if plan.popular}
            <div class="absolute top-0 left-0 right-0 bg-white text-black text-center py-2 text-xs uppercase tracking-wider">
              Most Popular
            </div>
          {/if}

          <div class="p-6 md:p-8 {plan.popular ? 'pt-12 md:pt-14' : ''} flex flex-col h-full">
            <h3 class="text-white mb-2 text-lg md:text-xl">{plan.name}</h3>
            <div class="mb-3 md:mb-4">
              <span class="text-4xl md:text-5xl text-white font-light" style="font-family: var(--font-serif)">
                ${plan.price}
              </span>
              <span class="text-slate-500 text-xs md:text-sm">/forever</span>
            </div>
            <p class="text-slate-500 mb-6 md:mb-8 text-xs md:text-sm leading-relaxed">{plan.description}</p>

            <ul class="space-y-2 md:space-y-3 mb-6 md:mb-8 flex-grow">
              {#each plan.features as feature}
                <li class="flex items-start gap-2 md:gap-3">
                  <Check class="w-3.5 h-3.5 md:w-4 md:h-4 text-white flex-shrink-0 mt-0.5" />
                  <span class="text-slate-400 text-xs md:text-sm">{feature}</span>
                </li>
              {/each}
            </ul>

            <button
              on:click={() => openBuyModal(plan)}
              class="w-full px-5 md:px-6 py-2.5 md:py-3 rounded-md transition-all hover:-translate-y-0.5 text-xs md:text-sm {plan.popular ? 'bg-white text-black' : 'bg-white/10 text-white hover:bg-white/15'} mt-auto"
              style={plan.popular ? 'box-shadow: 0 4px 14px 0 rgba(255, 255, 255, 0.2)' : ''}
            >
              Buy Now
            </button>
          </div>
        </div>
      {/each}
    </div>
  </div>
</section>

<!-- Buy Now Modal -->
{#if showBuyModal && selectedPlan}
  <div
    class="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    on:click={closeBuyModal}
    on:keydown={(e) => e.key === 'Escape' && closeBuyModal()}
    role="button"
    tabindex="0"
  >
    <div
      class="bg-zinc-900 border border-white/10 rounded-xl max-w-md w-full p-6 md:p-8 relative"
      on:click={(e) => e.stopPropagation()}
      on:keydown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      tabindex="-1"
    >
      <!-- Close Button -->
      <button
        on:click={closeBuyModal}
        class="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
        aria-label="Close modal"
      >
        <X class="w-5 h-5" />
      </button>

      <!-- Modal Header -->
      <div class="mb-6">
        <h3 class="text-white text-2xl md:text-3xl mb-2">
          Buy {selectedPlan.name} API Key
        </h3>
        <p class="text-slate-400 text-sm">
          Enter your email to complete the purchase
        </p>
      </div>

      <!-- Order Summary -->
      <div class="bg-white/5 rounded-lg p-4 mb-6 border border-white/10">
        <div class="flex justify-between items-center mb-2">
          <span class="text-slate-400 text-sm">Product</span>
          <span class="text-white text-sm">{selectedPlan.name} API Key</span>
        </div>
        <div class="flex justify-between items-center">
          <span class="text-slate-400 text-sm">Price</span>
          <span class="text-white text-lg font-semibold">${selectedPlan.price}</span>
        </div>
      </div>

      <!-- Email Input -->
      <div class="mb-6">
        <label for="customer-email" class="block text-slate-400 text-sm mb-2">
          Email Address
        </label>
        <input
          id="customer-email"
          type="email"
          bind:value={customerEmail}
          on:keypress={handleKeyPress}
          placeholder="[email protected]"
          class="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-white/30 transition-colors"
          disabled={isProcessing}
          autocomplete="email"
        />
        <p class="text-slate-500 text-xs mt-2">
          You'll receive your API key and order details at this email
        </p>
      </div>

      <!-- Error Message -->
      {#if errorMessage}
        <div class="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p class="text-red-400 text-sm">{errorMessage}</p>
        </div>
      {/if}

      <!-- Action Buttons -->
      <div class="flex gap-3">
        <button
          on:click={closeBuyModal}
          disabled={isProcessing}
          class="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          on:click={handleBuyNow}
          disabled={isProcessing || !customerEmail}
          class="flex-1 px-4 py-3 bg-white text-black hover:bg-white/90 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? 'Processing...' : 'Continue to Payment'}
        </button>
      </div>

      <!-- Security Notice -->
      <p class="text-slate-500 text-xs text-center mt-4">
        Secure payment powered by OxaPay. You'll be redirected to complete the payment.
      </p>
    </div>
  </div>
{/if}
